import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { getCounty, FEATURED_SEARCHES } from "@/data";
import { LeadDialog } from "@/components/LeadForm";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQS = [
  ["Do you charge for a consultation?", "No. Initial consultations and discovery calls are complimentary and no-pressure."],
  ["Can you help with new construction?", "Yes. Richard can represent you with builders and help navigate contracts and incentives."],
  ["Do you work with first-time buyers?", "Absolutely — including guidance on preapproval, budgeting, and available programs."],
  ["Is the listing data live MLS?", "No. Listings shown are sample/placeholder data. An approved MLS/IDX feed can be connected later."],
];

export default function County() {
  const { slug } = useParams();
  const c = getCounty(slug);
  if (!c) return <div className="pt-40 text-center pb-40">County not found. <Link to="/areas" className="text-gold underline">Back to areas</Link></div>;

  return (
    <div className="bg-cream pt-24">
      <section className="relative h-[52vh] min-h-[380px] overflow-hidden">
        <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/40 to-navy/20" />
        <div className="relative max-w-7xl mx-auto h-full flex flex-col justify-end px-6 md:px-12 pb-12 text-cream">
          <Reveal><p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-3">Metro Atlanta</p></Reveal>
          <Reveal delay={0.1}><h1 className="font-serif text-5xl md:text-7xl tracking-tighter">{c.name}</h1></Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 py-16 max-w-7xl mx-auto grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          <Reveal>
            <h2 className="font-serif text-3xl text-navy mb-3">County overview</h2>
            <p className="text-charcoal/75 text-lg leading-relaxed">{c.desc}</p>
          </Reveal>
          <Reveal>
            <h3 className="font-serif text-2xl text-navy mb-3">General housing types</h3>
            <p className="text-charcoal/75">{c.housing}</p>
          </Reveal>
          <Reveal>
            <h3 className="font-serif text-2xl text-navy mb-4">Featured cities</h3>
            <div className="flex flex-wrap gap-3">
              {c.cities.map((city) => (
                <Link key={city} to={`/areas/${c.slug}/${city.toLowerCase().replace(/\s/g, "-")}`} data-testid={`city-link-${city.toLowerCase().replace(/\s/g, "-")}`}
                  className="inline-flex items-center gap-1.5 border border-navy/15 rounded-full px-4 py-2 text-sm font-medium text-navy hover:bg-navy hover:text-cream transition-colors">
                  <MapPin size={14} /> {city}
                </Link>
              ))}
            </div>
          </Reveal>
          <Reveal>
            <h3 className="font-serif text-2xl text-navy mb-3">New-construction opportunities</h3>
            <p className="text-charcoal/75">{c.name} continues to see new residential development. Richard can help you evaluate builder communities and available incentives. <span className="text-charcoal/50">(Placeholder — specific communities available on request.)</span></p>
          </Reveal>
          <Reveal>
            <h3 className="font-serif text-2xl text-navy mb-4">Search this county</h3>
            <div className="flex flex-wrap gap-3">
              {FEATURED_SEARCHES.slice(0, 5).map((s) => (
                <Link key={s.label} to={`/search?county=${c.slug}`} className="border border-navy/15 rounded-full px-4 py-2 text-sm text-navy hover:bg-navy hover:text-cream transition-colors">{s.label}</Link>
              ))}
            </div>
          </Reveal>
          <Reveal>
            <h3 className="font-serif text-2xl text-navy mb-4">Frequently asked questions</h3>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map(([q, a], i) => (
                <AccordionItem key={i} value={`f${i}`}>
                  <AccordionTrigger className="text-left font-medium text-navy" data-testid={`faq-${i}`}>{q}</AccordionTrigger>
                  <AccordionContent className="text-charcoal/70">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>

        <aside className="space-y-4">
          <div className="bg-white rounded-3xl p-8 border border-black/5 sticky top-28">
            <h3 className="font-serif text-2xl text-navy mb-4">Ready to move here?</h3>
            <div className="flex flex-col gap-3">
              <Link to="/buy/questionnaire" data-testid="county-buy-cta" className="bg-gold text-navy font-semibold text-center py-3 rounded-full hover:bg-gold/90 transition-colors">I want to buy</Link>
              <Link to="/sell" data-testid="county-sell-cta" className="bg-navy text-cream font-semibold text-center py-3 rounded-full hover:bg-navy/90 transition-colors">I want to sell</Link>
              <LeadDialog type="consultation" source={`county-${c.slug}-relocate`} title="Relocation Consultation" defaults={{ county: c.name }}
                trigger={<Button data-testid="county-relocate-cta" variant="outline" className="border-navy/20 text-navy font-semibold rounded-full w-full">Relocation consultation</Button>} />
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
